/**
 * ThermasolEngine/Debug/DebugCollector.ts — Vague 17.1
 * ----------------------------------------------------------------
 * API que les moteurs appellent pour émettre des traces.
 *
 * USAGE dans un moteur (ex. CEEEngine) :
 *
 *   import { DebugCollector as dbg } from "../Debug";
 *
 *   dbg.trace(() => ({
 *     id: "cee.prime_cee_eur",
 *     engine: "CEEEngine",
 *     engine_version: CEE_ENGINE_VERSION,
 *     label: "Prime CEE versée client (50%)",
 *     formula: "prime_client = cumac × 0.0078 × 0.50",
 *     inputs: { cumac_kwh, valeur_kwh_cumac, part_client_cee },
 *     result: primeClient,
 *     result_unit: "€",
 *     severity: "PASS",
 *     norms: [{ code: "AGRI-EQ-110", label: "CEE agricole" }],
 *     source: { engine: "CEEEngine", engine_version: CEE_ENGINE_VERSION, ... },
 *     scope: { building_id, building_label },
 *   }));
 *
 * PERFORMANCE :
 *   Si `DebugRegistry.enabled === false`, le callback n'est PAS
 *   évalué → coût ≈ 1 comparaison booléenne. Idéal en prod.
 */

import { DebugRegistry } from "./DebugRegistry";
import type {
  FormulaTrace, RuleTrace, WarningTrace, SelfCheckResult,
} from "./DebugTypes";
import { nowIso, makeTraceId } from "./DebugTypes";

// Trace payload sans timestamp/id (le collector les remplit)
type FormulaInput = Omit<FormulaTrace, "timestamp"> & { timestamp?: string };
type RuleInput   = Omit<RuleTrace, "timestamp"> & { timestamp?: string };
type WarningInput = Omit<WarningTrace, "timestamp"> & { timestamp?: string };

export const DebugCollector = {
  /** True si le mode Debug est actif. */
  get enabled(): boolean {
    return DebugRegistry.enabled;
  },

  /**
   * Trace une FORMULE. Le callback n'est pas évalué si debug OFF.
   * Retourne la trace ingérée ou null.
   */
  trace(build: () => FormulaInput): FormulaTrace | null {
    if (!DebugRegistry.enabled) return null;
    try {
      const raw = build();
      const trace: FormulaTrace = {
        ...raw,
        timestamp: raw.timestamp || nowIso(),
      } as FormulaTrace;
      // Self-check optionnel intégré (basique) : si aucun self_check fourni
      // et si les entrées permettent une recomputation naïve → skip.
      DebugRegistry.pushFormula(trace);
      return trace;
    } catch (e) {
      // On n'interrompt JAMAIS le calcul réel à cause du Debug
      try { console.warn("[DebugCollector.trace] threw", e); } catch { /* noop */ }
      return null;
    }
  },

  /** Trace une RÈGLE (Eurocode/CEE/HVAC/…) */
  rule(build: () => RuleInput): RuleTrace | null {
    if (!DebugRegistry.enabled) return null;
    try {
      const raw = build();
      const trace: RuleTrace = {
        ...raw,
        timestamp: raw.timestamp || nowIso(),
      } as RuleTrace;
      DebugRegistry.pushRule(trace);
      return trace;
    } catch (e) {
      try { console.warn("[DebugCollector.rule] threw", e); } catch { /* noop */ }
      return null;
    }
  },

  /** Trace un WARNING/ERREUR avec propositions de diagnostic. */
  warn(build: () => WarningInput): WarningTrace | null {
    if (!DebugRegistry.enabled) return null;
    try {
      const raw = build();
      const trace: WarningTrace = {
        ...raw,
        timestamp: raw.timestamp || nowIso(),
      } as WarningTrace;
      DebugRegistry.pushWarning(trace);
      return trace;
    } catch (e) {
      try { console.warn("[DebugCollector.warn] threw", e); } catch { /* noop */ }
      return null;
    }
  },

  /**
   * Vérifie qu'une formule numérique retrouve bien la valeur affichée.
   * À appeler juste après un calcul pour capturer un self-check.
   */
  selfCheck(
    expected: number,
    actual: number,
    recomputedFrom: string,
    tolerancePct = 0.01,
  ): SelfCheckResult {
    const diff = Math.abs(actual - expected);
    const denom = Math.max(Math.abs(expected), 1e-9);
    const pct = (diff / denom) * 100;
    return {
      ok: pct <= tolerancePct,
      expected_value: expected,
      actual_value: actual,
      diff_abs: diff,
      diff_pct: Math.round(pct * 10000) / 10000,
      tolerance_pct: tolerancePct,
      recomputed_from: recomputedFrom,
      reason: pct > tolerancePct
        ? `Écart ${pct.toFixed(4)}% > tolérance ${tolerancePct}%`
        : undefined,
    };
  },

  /** Session lifecycle (typiquement appelée par le context builder). */
  beginSession(projectRef?: string) {
    if (!DebugRegistry.enabled) return null;
    return DebugRegistry.beginSession(projectRef);
  },

  endSession() {
    if (!DebugRegistry.enabled) return null;
    return DebugRegistry.endSession();
  },

  /** Perf tracking par moteur. */
  markEngineStart(engine: string) {
    DebugRegistry.markEngineStart(engine);
  },
  markEngineEnd(engine: string) {
    DebugRegistry.markEngineEnd(engine);
  },

  /** Utilitaire : crée un id trace. */
  newId(prefix?: string) { return makeTraceId(prefix); },
};

export default DebugCollector;
